import { Copy, RotateCcw, Save } from "lucide-react";
import { useState } from "react";

import {
  useAlertDestinationRegistryQuery,
  useAssetVersionQuery,
  useCreateAssetVersionMutation,
  type LibraryAsset,
  type LibraryAssetVersionDetail,
} from "@/api/library";
import { useCapability } from "@/auth/SessionContext";
import {
  attributeMatrixFailure,
  findMatrixWarnings,
  isAlertMatrixContent,
  isSameMatrixDraft,
  toAlertMatrixContent,
  toMatrixDraft,
  type DraftAlertMatrix,
  type MatrixProblem,
} from "@/lib/alertMatrix";
import type { SaveStatus } from "@/state/dirtyState";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardFooter, CardHeader, CardTitle } from "@/ui/Card";
import { Field, Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { ErrorState, LoadingState, StaleDataNotice } from "@/ui/states";
import { FieldAnchorProvider } from "@/ui/validation/FieldAnchorContext";

import { AlertMatrixEditor } from "./AlertMatrixEditor";
import {
  ConflictRecovery,
  SaveStatusLine,
  classifyPublishFailure,
  editRefusal,
} from "./AssetEditor";
import { VersionContent } from "./VersionContent";

/**
 * The authoring surface for one alert-matrix asset (#35).
 *
 * Sibling to `AssetEditor` rather than a mode of it. The two share everything
 * that is a conversation with the SERVICE — the refusal order, the publish
 * classification, the stale-version recovery, the save status — and share none
 * of what is a conversation about CONTENT, because an alert matrix is a typed
 * document with its own invariants rather than an ordered list of pack files.
 *
 * SAVE IS A PUBLISH, and THE PIN IS TAKEN AT LOAD, exactly as in #33. The
 * `expectedHeadVersion` sent with a Save is the version this clinician read and
 * edited; re-reading it at submit time would make the concurrency check a
 * formality that always passes and always clobbers. A background refetch never
 * re-seeds a dirty draft, so the collision surfaces at Save with a clinician
 * present to resolve it.
 *
 * READ-ONLY IS A DIFFERENT COMPONENT. When the asset refuses edits — seeded base
 * canon, a locked guard floor, an archived asset, a reader's session — this
 * renders the reason and `VersionContent`. Not a disabled form: a form with
 * every control disabled is one forgotten prop away from being live, and the
 * locked safety floor is the one place in this application where that must be
 * impossible by construction rather than by review.
 *
 * WHAT THE LOCKED FLOOR DOES AND DOES NOT GUARANTEE. `lockedFloor` is an
 * ASSET-level guard: the seeded floor cannot be archived, cannot be edited in
 * place, and stays adoptable. It is not a VALUE-level floor — no code anywhere
 * compares an authored threshold against a base minimum, `locked_keys` is empty
 * and `GUARD_FLOOR_ENFORCED` defaults off. A clinician may still author a
 * SEPARATE matrix with weaker thresholds and pin that. Value-level enforcement
 * is liv-brain#92/#133; nothing here should be read as claiming it.
 */

const NOTE_MAX_LENGTH = 500;

/** `v7` -> 7. Only ever compared — the server allocates the next label. */
function versionNumber(label: string): number {
  return Number.parseInt(label.slice(1), 10);
}

interface MatrixSession {
  pinned: string;
  loaded: DraftAlertMatrix;
  draft: DraftAlertMatrix;
  note: string;
}

export function AlertMatrixAssetEditor({
  asset,
  onCopy,
}: {
  asset: LibraryAsset;
  /** Opens the create dialog seeded from this asset, or null when it cannot. */
  onCopy: (() => void) | null;
}) {
  const write = useCapability("library.write");
  const head = useAssetVersionQuery(asset.id, asset.latestVersion);
  const publish = useCreateAssetVersionMutation(asset.id);
  const destinationRegistry = useAlertDestinationRegistryQuery();
  const destinationAuthoringAvailable =
    destinationRegistry.isSuccess &&
    destinationRegistry.data.entries.some((entry) => entry.lifecycle === "active");

  const [session, setSession] = useState<MatrixSession | null>(null);
  const [clientProblems, setClientProblems] = useState<MatrixProblem[]>([]);
  const [mappingProblem, setMappingProblem] = useState<MatrixProblem | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  const detail: LibraryAssetVersionDetail | undefined = head.data;
  const dirty =
    session !== null && !isSameMatrixDraft(session.draft, session.loaded);

  // Seeded during render, and only ever FORWARD — the same two guards #33's
  // editor carries, for the same two reasons: a dirty draft is never
  // overwritten by a background refetch, and the window in which the asset read
  // still names the version a publish just replaced never seeds backwards.
  let headMappingProblem: MatrixProblem | null = null;
  if (detail && isAlertMatrixContent(detail.content)) {
    const newer =
      session !== null &&
      versionNumber(detail.version) > versionNumber(session.pinned);
    if (session === null || (newer && !dirty)) {
      const mapped = toMatrixDraft(detail.content);
      if (mapped.ok) {
        setSession({
          pinned: detail.version,
          loaded: mapped.draft,
          draft: mapped.draft,
          note: "",
        });
        setClientProblems([]);
        setMappingProblem(null);
      } else {
        headMappingProblem = mapped.problem;
      }
    }
  }

  if (session === null) {
    if (head.isPending) {
      return <LoadingState label="Loading alert matrix" rows={4} />;
    }
    if (head.isError) {
      return (
        <ErrorState
          title="This matrix could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          onRetry={() => void head.refetch()}
        />
      );
    }
    if (headMappingProblem) {
      return (
        <ErrorState
          title="This matrix cannot be opened safely"
          description={headMappingProblem.message}
        />
      );
    }
  }

  if (detail && !isAlertMatrixContent(detail.content)) {
    // Defensive: the asset type and the content kind are decided together by
    // the service, so this is unreachable through the contract. Rendering the
    // content read-only beats rendering a matrix editor over something that is
    // not a matrix.
    return (
      <Card>
        <CardHeader>
          <CardTitle>Content at {detail.version} (read-only)</CardTitle>
        </CardHeader>
        <CardBody>
          <VersionContent content={detail.content} />
        </CardBody>
      </Card>
    );
  }

  if (session === null) {
    return <LoadingState label="Preparing the editor" rows={4} />;
  }

  const refusal = editRefusal(asset, write.reason);
  const readOnly = refusal !== null;
  const failure = publish.isError
    ? classifyPublishFailure(publish.error, asset.latestVersion)
    : null;

  // `kind: "parts"` is #33's name for "the service refused the CONTENT with a
  // field pointer into it" — the envelope is identical for a matrix, so the
  // classification is shared and only the attribution is family-specific.
  const serverProblems: MatrixProblem[] =
    failure?.kind === "parts"
      ? [
          {
            path: attributeMatrixFailure(failure.message) ?? "content",
            message: failure.message,
          },
        ]
      : [];

  const warnings = findMatrixWarnings(session.draft);

  const status: SaveStatus = publish.isPending
    ? "saving"
    : failure !== null
      ? "error"
      : dirty
        ? "dirty"
        : lastSavedAt
          ? "saved"
          : "clean";

  function updateDraft(draft: DraftAlertMatrix) {
    setSession((current) => (current ? { ...current, draft } : current));
    // Last attempt's refusals describe content that no longer exists.
    setClientProblems([]);
    publish.reset();
  }

  function discard() {
    setSession((current) =>
      current ? { ...current, draft: current.loaded, note: "" } : current,
    );
    setClientProblems([]);
    publish.reset();
  }

  function save() {
    const current = session;
    if (!current) {
      return;
    }
    const result = toAlertMatrixContent(current.draft);
    if (!result.ok) {
      setClientProblems(result.problems);
      return;
    }
    setClientProblems([]);
    publish.mutate(
      {
        expectedHeadVersion: current.pinned,
        content: result.content,
        note: current.note.trim() === "" ? null : current.note.trim(),
      },
      {
        onSuccess(published) {
          if (!isAlertMatrixContent(published.content)) {
            setMappingProblem({
              path: "content",
              message:
                "The published response was not an alert matrix. The existing draft was kept and was not replaced.",
            });
            return;
          }
          const mapped = toMatrixDraft(published.content);
          if (!mapped.ok) {
            setMappingProblem(mapped.problem);
            return;
          }
          setSession({
            pinned: published.version,
            loaded: mapped.draft,
            draft: mapped.draft,
            note: "",
          });
          setMappingProblem(null);
          setLastSavedAt(published.createdAt);
        },
      },
    );
  }

  if (readOnly) {
    return (
      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Content at {session.pinned} (read-only)</CardTitle>
          {asset.capabilities.seeded && onCopy ? (
            // Creating the copy needs `library.write`, which the route cannot
            // know when it wires the handler. Without it the clinician could
            // author an entire matrix and only meet the 403 at Create, so the
            // control is disabled rather than enabled-and-doomed. The reason is
            // already on screen: `editRefusal` returns the capability reason
            // ahead of everything else, and it is the paragraph below. Reachable
            // only since #494 supplied the handler — the prop was `null`, so
            // this button never rendered at all.
            <Button
              variant="outline"
              size="sm"
              onClick={onCopy}
              disabled={write.reason !== null}
            >
              <Copy aria-hidden="true" className="h-4 w-4" />
              Copy into a new asset
            </Button>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
            {refusal}
          </p>
          {detail ? <VersionContent content={detail.content} /> : null}
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Editing {session.pinned} · publishing creates the next version
        </CardTitle>
      </CardHeader>

      <CardBody className="space-y-4">
        <StaleDataNotice
          updatedAt={head.dataUpdatedAt}
          onRetry={() => void head.refetch()}
          visible={head.isError}
        />

        {headMappingProblem || mappingProblem ? (
          <Notice role="alert" tone="destructive">
            {(headMappingProblem ?? mappingProblem)?.message} The draft already
            on screen was kept unchanged.
          </Notice>
        ) : null}

        {failure?.kind === "stale" ? (
          <ConflictRecovery
            assetId={asset.id}
            version={failure.version}
            onRebase={() => {
              setSession((current) =>
                current ? { ...current, pinned: failure.version } : current,
              );
              publish.reset();
            }}
            onDiscardMine={(newer) => {
              if (!isAlertMatrixContent(newer.content)) {
                setMappingProblem({
                  path: "content",
                  message:
                    "The newer version was not an alert matrix, so it cannot replace this draft.",
                });
                return;
              }
              const mapped = toMatrixDraft(newer.content);
              if (!mapped.ok) {
                setMappingProblem(mapped.problem);
                return;
              }
              setSession({
                pinned: newer.version,
                loaded: mapped.draft,
                draft: mapped.draft,
                note: "",
              });
              setClientProblems([]);
              setMappingProblem(null);
              publish.reset();
            }}
          />
        ) : null}

        {failure?.kind === "refused" ? (
          <Notice role="alert" tone="destructive">
            {failure.message}
          </Notice>
        ) : null}

        {/* One provider per asset surface, so a finding resolves against the
            controls actually on screen and unmounting takes its anchors with
            it. */}
        <FieldAnchorProvider>
          <AlertMatrixEditor
            draft={session.draft}
            onChange={updateDraft}
            problems={[...clientProblems, ...serverProblems]}
            warnings={warnings}
            destinationRegistry={destinationRegistry.data ?? null}
            destinationRegistryState={
              destinationRegistry.isPending
                ? "pending"
                : destinationRegistry.isSuccess
                  ? "ready"
                  : "error"
            }
            onRetryDestinationRegistry={() => {
              void destinationRegistry.refetch();
            }}
          />
        </FieldAnchorProvider>
      </CardBody>

      <CardFooter className="flex-wrap justify-between gap-3">
        <div className="min-w-[16rem] flex-1">
          <Field
            label="Version note"
            description="Recorded against the version this Save creates, and shown in the history beside it."
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                value={session.note}
                maxLength={NOTE_MAX_LENGTH}
                aria-describedby={describedBy}
                placeholder="What changed, and why"
                onChange={(event) =>
                  setSession((current) =>
                    current ? { ...current, note: event.target.value } : current,
                  )
                }
              />
            )}
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <SaveStatusLine status={status} lastSavedAt={lastSavedAt} />
          <Button
            variant="outline"
            disabled={!dirty || publish.isPending}
            onClick={discard}
          >
            <RotateCcw aria-hidden="true" className="h-4 w-4" />
            Discard changes
          </Button>
          <Button
            disabled={!dirty || publish.isPending || !destinationAuthoringAvailable}
            onClick={save}
          >
            <Save aria-hidden="true" className="h-4 w-4" />
            Publish next version
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
